import { createContext, useContext } from 'react';

/**
 * Runtime config loaded from `/config.json` at startup (admin-shell-oidc-rbac
 * §4.2/LD-2) — NOT baked VITE_* envs — so one static build runs in every
 * environment. There is NO client secret here (a public PKCE client).
 */
export interface AdminRuntimeConfig {
  readonly issuer: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly postLogoutRedirectUri: string;
  readonly apiBase: string;
  readonly scope: string;
  /**
   * Origin that serves the public `/media/<key>` bundle (edge/CloudFront in
   * dev/prod). OPTIONAL: when omitted, media URLs stay relative — correct
   * locally (the same-origin gateway proxies `/media`) and a safe no-regression
   * default elsewhere (previews fall back to "No image"). Set per-env to the
   * real media origin so admin thumbnails resolve. See `app/lib/media-url.ts`.
   */
  readonly mediaBase?: string;
  /**
   * True when offer-system has the GiG Magic X write-through switched on
   * (its `MAGICX_ENABLED`). OPTIONAL, treated as false when absent — so existing
   * environments keep their current behaviour with no config change.
   *
   * When true, `skuId` is SERVER-OWNED: GiG mints the promotion id during the
   * create and the service ignores any value we send, so the offer editor renders
   * the field read-only. When false, the SKU is still caller-supplied and the
   * service REQUIRES it.
   *
   * This must match the service's setting. A mismatch is user-visible either way:
   * stale-false offers an editable field whose value is silently discarded;
   * stale-true hides a field the service then rejects the save for.
   */
  readonly magicxEnabled?: boolean;
}

export async function loadRuntimeConfig(
  fetchFn: typeof fetch = fetch,
): Promise<AdminRuntimeConfig> {
  const res = await fetchFn('/config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to load /config.json (HTTP ${res.status}).`);
  }
  return (await res.json()) as AdminRuntimeConfig;
}

/**
 * Exported so lenient readers (e.g. `useMediaSrc` in `app/lib/media-url.ts`) can
 * read config WITHOUT the throw of `useRuntimeConfig` — a component rendered
 * outside a provider (notably in unit tests) then degrades gracefully instead of
 * crashing.
 */
export const RuntimeConfigContext = createContext<AdminRuntimeConfig | null>(null);

export const RuntimeConfigProvider = RuntimeConfigContext.Provider;

export function useRuntimeConfig(): AdminRuntimeConfig {
  const config = useContext(RuntimeConfigContext);
  if (!config) {
    throw new Error('useRuntimeConfig must be used within a RuntimeConfigProvider.');
  }
  return config;
}
